"use client";
import React, { useState } from "react";
import { Form, Input, Checkbox, notification } from "antd";
import { submitLeadForm } from "@/lib/submit-form";
import {
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaCommentDots,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { Constant } from "@/constants/constants";
import { SectionHeading } from "@/components/common/decor";

const { TextArea } = Input;

const ContactFormLayout = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values) => {
    if (submitting) return;
    setSubmitting(true);
    const res = await submitLeadForm(values, "Contact Us — Contact Form");
    if (res.ok) {
      window.location.assign("/thankyou");
      return;
    }
    setSubmitting(false);
    notification.error({ message: "Could not send your message", description: res.message });
  };

  const fieldClass =
    "!h-12 !rounded-xl !bg-[#F1F8FC] hover:!bg-white focus-within:!bg-white !border !border-ink/10 hover:!border-gold/50 focus-within:!border-brand-500 !shadow-none !text-ink !text-[15px] transition-all duration-200";

  const details = [
    { icon: <FaEnvelope />, label: Constant.Email, href: Constant.EmailLink },
    { icon: <FaPhoneAlt />, label: Constant.PhoneNumber, href: Constant.PhoneNumberLink },
    { icon: <FaMapMarkerAlt />, label: Constant.Address, href: null },
  ];

  return (
    <section className="bg-paper bg-paper-grain px-4 py-16 md:py-20">
      <div className="mx-auto max-w-[1350px]">
        <SectionHeading
          eyebrow="Contact Us"
          title="Let's Talk About Your Book"
          description="Every project starts with a conversation. Whether you're early in the process or ready to move forward, our team will help figure out the right starting point together."
          className="mb-14"
        />

        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Form */}
          <div className="hero-lead-form rounded-3xl border border-ink/10 bg-white p-6 shadow-editorial sm:p-8">
            <span className="eyebrow text-gold-deep">Free Consultation</span>
            <h2 className="mt-2 font-display text-2xl font-semibold text-ink sm:text-3xl">Send Us a Message</h2>

            <Form form={form} name="contact_form" onFinish={onFinish} layout="vertical" className="mt-6">
              <Form.Item name="name" rules={[{ required: true, message: "Please input your Name!" }, { min: 2, message: "Name must be at least 2 characters!" }]} className="!mb-4">
                <Input prefix={<span className="mr-2 text-ink-soft"><FaUser /></span>} placeholder="Your Name" className={fieldClass} />
              </Form.Item>
              <Form.Item name="email" rules={[{ type: "email", message: "The input is not valid E-mail!" }, { required: true, message: "Please input your E-mail!" }]} className="!mb-4">
                <Input prefix={<span className="mr-2 text-ink-soft"><FaEnvelope /></span>} placeholder="Your Email Address" className={fieldClass} />
              </Form.Item>
              <Form.Item
                name="phone"
                rules={[{ pattern: /^\+?[\d\s\-\(\)]+$/, message: "Please enter a valid phone number!" }]}
                className="!mb-2"
              >
                <Input prefix={<span className="mr-2 text-ink-soft"><FaPhoneAlt /></span>} placeholder="Your Phone Number" className={fieldClass} />
              </Form.Item>
              <p className="mb-4 rounded-lg border border-ink/10 bg-[#F1F8FC] px-3 py-2 text-[11px] leading-snug text-ink-soft">
                By submitting your phone number, you agree to receive SMS messages from {Constant.CompanyName} about your inquiry and services. Message frequency varies; message &amp; data rates may apply. Reply HELP for help or STOP to cancel. Consent isn&apos;t required to make a purchase.
              </p>
              <Form.Item name="message" rules={[{ required: true, message: "Please input your message!" }]} className="!mb-5">
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-3.5 z-10 text-ink-soft"><FaCommentDots /></span>
                  <TextArea placeholder="I am looking for..." rows={4} className="!rounded-xl !bg-[#F1F8FC] hover:!bg-white focus:!bg-white !border !border-ink/10 hover:!border-gold/50 focus:!border-brand-500 !shadow-none !pl-11 !pt-2.5 !text-ink !text-[15px] transition-all duration-200" />
                </div>
              </Form.Item>
              <Form.Item name="smsConsent" valuePropName="checked" className="!mb-5">
                <Checkbox className="items-start text-xs leading-relaxed text-ink-soft [&_.ant-checkbox]:mt-0.5">
                  I agree to receive SMS messages from {Constant.CompanyName} as described above. See our{" "}
                  <a href="/privacy-policy" target="_blank" className="text-brand hover:underline">Privacy Policy</a> and{" "}
                  <a href="/terms-and-conditions#sms-terms" target="_blank" className="text-brand underline">Terms & Conditions</a>.
                </Checkbox>
              </Form.Item>
              <Form.Item className="!mb-0">
                <button type="submit" disabled={submitting} className="flex h-12 w-full items-center justify-center rounded-xl bg-brand-800 font-semibold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-900 disabled:opacity-60">
                  {submitting ? "Sending..." : "Submit Your Query"}
                </button>
              </Form.Item>
            </Form>
          </div>

          {/* Details */}
          <div>
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl">Contact Details</h2>
            <div className="mt-6 space-y-4">
              {details.map((d, i) => {
                const inner = (
                  <div className="group flex items-center gap-4 rounded-2xl border border-ink/10 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-editorial">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700 transition-colors group-hover:bg-brand-600 group-hover:text-white">
                      {d.icon}
                    </span>
                    <span className="break-words font-medium text-ink max-sm:text-sm">{d.label}</span>
                  </div>
                );
                return d.href ? (
                  <a key={i} href={d.href} className="block">{inner}</a>
                ) : (
                  <div key={i}>{inner}</div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormLayout;
